import { useCallback, useRef } from 'react'

import type { ErrorLike } from '@apollo/client'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

import { useTypedMutation } from '~publish/hooks/useTypedMutation'

import type { CommentData } from '../../Comments/fragments/commentData'

import { triggerReplyCountIncreased } from '../../Comments/CommentsList/utils/commentScoreSessionManager'
import { incrementSavedRepliesCoachmarkCount } from '../ui/useSavedRepliesCoachmark'
import { CommentLastReply_LastReply } from '../../Comments/fragments/commentLastReply'

export const ReplyToCommentGQL = graphql(
  /* GraphQL */ `
    mutation ReplyToComment($input: ReplyToCommentInput!) {
      replyToComment(input: $input) {
        __typename
        ... on ReplyToCommentSuccess {
          comment {
            ... on BaseComment {
              id
              text
              publishedAt
              status
              parentId
              publicUrl
              author {
                username
                avatar
                __typename
              }
              assets {
                id
                source
                thumbnail
                type
                mimeType
              }
              lastReplies {
                ...CommentLastReply_LastReply
              }
            }
            ... on CommentFromBuffer {
              id
              text
              publishingStatus {
                errorMessage
                status
              }
              author {
                username
                avatar
              }
              publishedAt
              status
              assets {
                id
                type
                source
                thumbnail
              }
            }
            ... on CommentFromThirdParty {
              id
              text
              author {
                username
                avatar
              }
              publishedAt
              status
              assets {
                id
                type
                source
                thumbnail
              }
            }
          }
        }
        ... on NotFoundError {
          message
        }
        ... on UnauthorizedError {
          message
        }
        ... on UnexpectedError {
          message
        }
        ... on InvalidInputError {
          message
        }
      }
    }
  `,
  [CommentLastReply_LastReply],
)

// The comment shape actually selected by the ReplyToComment mutation's success
// branch. This is narrower than CommentData (the full union) — only the fields
// the mutation selects are present.
export type ReplyToCommentResultComment = Extract<
  ResultOf<typeof ReplyToCommentGQL>['replyToComment'],
  { __typename: 'ReplyToCommentSuccess' }
>['comment']

// A reply optimistically returned by the ReplyToComment mutation. Narrower than
// the full LastReply type — only the fields the mutation selects are present.
// The full server LastReply is assignable to this, so consumers can hold both.
export type OptimisticReply = NonNullable<
  NonNullable<ReplyToCommentResultComment>['lastReplies']
>[number]

type UseReplyToCommentReturn = {
  replyToComment: (
    comment: CommentData,
    text: string,
  ) => Promise<{
    success: boolean
    data?: ReplyToCommentResultComment
    error?: string
    errorType?: string
    apolloError?: ErrorLike
  }>
  loading: boolean
  error?: Error | null
}

/**
 * Hook for executing comment reply GraphQL mutations.
 *
 * This hook provides a low-level interface for replying to comments:
 * - Executes the ReplyToComment GraphQL mutation
 * - Updates Apollo cache with the new reply data
 * - Changes parent comment status to 'replied'
 * - Keeps comments visible in the UI (no removal from lists)
 * - Returns structured success/error responses
 *
 * @returns Object containing replyToComment function, loading state, and error
 *
 * @example
 * ```typescript
 * const { replyToComment, loading, error } = useReplyToComment()
 *
 * const handleReply = async (comment, text) => {
 *   const result = await replyToComment(comment, text)
 *   if (result.success) {
 *     // Reply created successfully, cache updated
 *   } else {
 *     // Handle API error
 *   }
 * }
 * ```
 */
export const useReplyToComment = (): UseReplyToCommentReturn => {
  // useTypedMutation catches all thrown errors internally and never re-throws,
  // so hookError (React state) is always stale by the time we check it inside
  // an async callback. Capture the ApolloError synchronously via onError instead.
  const latestApolloErrorRef = useRef<ErrorLike | null>(null)
  const [mutate, { loading, error: hookError }] = useTypedMutation(
    ReplyToCommentGQL,
    (data) => data.replyToComment,
    {
      successTypename: 'ReplyToCommentSuccess',
      // no-cache: the response's comment shape doesn't match any query, so
      // automatic normalization is skipped. The reply form echoes the reply
      // from local state, and useCommentReplySubmitStrategy's syncCommentCache
      // writes the response's lastReplies through the cache for other
      // watchers (e.g. the list behind the comment-details dialog).
      fetchPolicy: 'no-cache',
      onError: (error) => {
        latestApolloErrorRef.current = error
      },
    },
  )

  const replyToComment = useCallback(
    async (comment: CommentData, text: string) => {
      // Reset so that onError can capture the ApolloError from this invocation
      latestApolloErrorRef.current = null

      const result = await mutate({
        variables: { input: { commentId: comment.id, reply: { text } } },
      })

      if (!result.success) {
        return {
          success: false,
          error: result.error.message,
          errorType: result.error.__typename,
          apolloError: latestApolloErrorRef.current ?? undefined,
        }
      }

      // Increment only on success so the coachmark count reflects actual sent replies
      incrementSavedRepliesCoachmarkCount()

      if (comment.channelId) {
        triggerReplyCountIncreased(comment.channelId)
      }

      return { success: true, data: result.data.comment }
    },
    [mutate],
  )

  return {
    replyToComment,
    loading,
    error:
      hookError && 'message' in hookError ? new Error(hookError.message) : null,
  }
}
